import React, { useState, useEffect } from 'react';
import axiosInstance from '../../utils/axios';
import API_URLS from '../../config/api';
import { 
  ChartBarIcon, 
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  UserGroupIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  DocumentArrowDownIcon,
  FunnelIcon,
  CalendarDaysIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

function AnalyticsReports() {
  const [analytics, setAnalytics] = useState({
    userGrowth: [],
    rideVolume: [],
    cancellationRates: [],
    totalUsers: 0,
    totalRides: 0,
    totalBookings: 0,
    averageRating: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('30days');
  const [selectedChart, setSelectedChart] = useState('userGrowth');

  useEffect(() => {
    fetchAnalytics();
  }, [selectedPeriod]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      
      // Fetch users data
      const usersResponse = await axiosInstance.get(API_URLS.admin.users);
      const users = usersResponse.data;
      
      // Fetch rides data
      const ridesResponse = await axiosInstance.get(API_URLS.admin.rides);
      const rides = ridesResponse.data;
      
      // Fetch bookings data
      const bookingsResponse = await axiosInstance.get(API_URLS.admin.bookings);
      const bookings = bookingsResponse.data;
      
      // Process data for analytics
      const processedAnalytics = processAnalyticsData(users, rides, bookings);
      setAnalytics(processedAnalytics);
      
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  const processAnalyticsData = (users, rides, bookings) => {
    // User Growth Data (last 12 months)
    const userGrowth = [];
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthYear = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      months.push(monthYear);
      
      const usersUntilMonth = users.filter(user => 
        new Date(user.createdAt) <= date
      ).length;
      userGrowth.push({
        month: monthYear,
        total: usersUntilMonth,
        new: users.filter(user => {
          const userDate = new Date(user.createdAt);
          return userDate.getMonth() === date.getMonth() && 
                 userDate.getFullYear() === date.getFullYear();
        }).length
      });
    }

    // Ride Volume Data (last 12 months)
    const rideVolume = [];
    for (let i = 11; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthYear = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      
      const ridesInMonth = rides.filter(ride => {
        const rideDate = new Date(ride.date);
        return rideDate.getMonth() === date.getMonth() && 
               rideDate.getFullYear() === date.getFullYear();
      }).length;
      
      rideVolume.push({
        month: monthYear,
        rides: ridesInMonth
      });
    }

    // Cancellation Rates Data
    const cancellationRates = [];
    for (let i = 11; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthYear = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      
      const totalRidesInMonth = rides.filter(ride => {
        const rideDate = new Date(ride.date);
        return rideDate.getMonth() === date.getMonth() && 
               rideDate.getFullYear() === date.getFullYear();
      }).length;
      
      const cancelledRidesInMonth = rides.filter(ride => {
        const rideDate = new Date(ride.date);
        return ride.status === 'cancelled' &&
               rideDate.getMonth() === date.getMonth() && 
               rideDate.getFullYear() === date.getFullYear();
      }).length;
      
      const rate = totalRidesInMonth > 0 ? (cancelledRidesInMonth / totalRidesInMonth * 100) : 0;
      
      cancellationRates.push({
        month: monthYear,
        rate: rate.toFixed(1),
        cancelled: cancelledRidesInMonth,
        total: totalRidesInMonth
      });
    }

    return {
      userGrowth,
      rideVolume,
      cancellationRates,
      totalUsers: users.length,
      totalRides: rides.length,
      totalBookings: bookings.length,
      averageRating: 4.2 // Placeholder since we don't have ratings yet
    };
  };

  const exportToCSV = (data, filename) => {
    const csvContent = "data:text/csv;charset=utf-8," + 
      Object.keys(data[0]).join(",") + "\n" +
      data.map(row => Object.values(row).join(",")).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    window.print();
  };

  const StatCard = ({ title, value, change, changeType, icon: Icon, color }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className={`${color} rounded-md p-3`}>
              <Icon className="h-6 w-6 text-white" />
            </div>
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">
                {title}
              </dt>
              <dd className="text-lg font-medium text-gray-900">
                {value}
              </dd>
            </dl>
          </div>
        </div>
        {change && (
          <div className="mt-4">
            <div className="flex items-center text-sm">
              {changeType === 'increase' ? (
                <ArrowTrendingUpIcon className="h-4 w-4 text-green-500 mr-1" />
              ) : (
                <ArrowTrendingDownIcon className="h-4 w-4 text-red-500 mr-1" />
              )}
              <span className={changeType === 'increase' ? 'text-green-600' : 'text-red-600'}>
                {change}
              </span>
              <span className="text-gray-500 ml-1">from last month</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const SimpleChart = ({ data, type, title }) => {
    if (!data || data.length === 0) return null;

    const maxValue = Math.max(...data.map(d => type === 'userGrowth' ? d.total : type === 'rideVolume' ? d.rides : d.rate));
    
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        <div className="space-y-3">
          {data.slice(-6).map((item, index) => (
            <div key={index} className="flex items-center">
              <div className="w-16 text-sm text-gray-600">
                {item.month}
              </div>
              <div className="flex-1 mx-4">
                <div className="bg-gray-200 rounded-full h-4">
                  <div 
                    className={`h-4 rounded-full ${
                      type === 'userGrowth' ? 'bg-blue-500' : 
                      type === 'rideVolume' ? 'bg-green-500' : 'bg-red-500'
                    }`}
                    style={{ 
                      width: `${((type === 'userGrowth' ? item.total : type === 'rideVolume' ? item.rides : item.rate) / maxValue) * 100}%` 
                    }}
                  ></div>
                </div>
              </div>
              <div className="w-16 text-sm text-gray-900 text-right">
                {type === 'userGrowth' ? item.total : 
                 type === 'rideVolume' ? item.rides : 
                 `${item.rate}%`}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-print-report max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics & Reports</h1>
          <p className="mt-2 text-sm text-gray-700">
            Monitor system performance and generate detailed reports
          </p>
          <p className="print-only mt-1 text-sm text-gray-600">
            Generated {new Date().toLocaleString()}
          </p>
        </div>
        <div className="no-print mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={() => exportToCSV(analytics.userGrowth, 'user-growth-report')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <DocumentArrowDownIcon className="-ml-1 mr-2 h-5 w-5" />
            Export CSV
          </button>
          <button
            onClick={exportToPDF}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <DocumentArrowDownIcon className="-ml-1 mr-2 h-5 w-5" />
            Export PDF
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <StatCard
          title="Total Users"
          value={analytics.totalUsers}
          change="+12%"
          changeType="increase"
          icon={UserGroupIcon}
          color="bg-blue-500"
        />
        <StatCard
          title="Total Rides"
          value={analytics.totalRides}
          change="+8%"
          changeType="increase"
          icon={CalendarIcon}
          color="bg-green-500"
        />
        <StatCard
          title="Total Bookings"
          value={analytics.totalBookings}
          change="+15%"
          changeType="increase"
          icon={ClockIcon}
          color="bg-purple-500"
        />
        <StatCard
          title="Avg Rating"
          value={analytics.averageRating}
          change="+0.2"
          changeType="increase"
          icon={ChartBarIcon}
          color="bg-orange-500"
        />
      </div>

      {/* Filters */}
      <div className="no-print bg-white shadow rounded-lg mb-8">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div>
              <label htmlFor="period" className="block text-sm font-medium text-gray-700">
                Time Period
              </label>
              <select
                id="period"
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
              >
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
                <option value="90days">Last 3 Months</option>
                <option value="12months">Last 12 Months</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="chart" className="block text-sm font-medium text-gray-700">
                Chart Type
              </label>
              <select
                id="chart"
                value={selectedChart}
                onChange={(e) => setSelectedChart(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
              >
                <option value="userGrowth">User Growth</option>
                <option value="rideVolume">Ride Volume</option>
                <option value="cancellationRates">Cancellation Rates</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <SimpleChart 
          data={analytics.userGrowth} 
          type="userGrowth" 
          title="User Growth Trend"
        />
        <SimpleChart 
          data={analytics.rideVolume} 
          type="rideVolume" 
          title="Ride Volume Trend"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <SimpleChart 
          data={analytics.cancellationRates} 
          type="cancellationRates" 
          title="Cancellation Rate Trend"
        />
        
        {/* Detailed Metrics Table */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Detailed Metrics</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-3 rounded">
                <div className="text-sm text-gray-600">New Users This Month</div>
                <div className="text-xl font-semibold text-gray-900">
                  {analytics.userGrowth[analytics.userGrowth.length - 1]?.new || 0}
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <div className="text-sm text-gray-600">Rides This Month</div>
                <div className="text-xl font-semibold text-gray-900">
                  {analytics.rideVolume[analytics.rideVolume.length - 1]?.rides || 0}
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-3 rounded">
                <div className="text-sm text-gray-600">Current Cancellation Rate</div>
                <div className="text-xl font-semibold text-gray-900">
                  {analytics.cancellationRates[analytics.cancellationRates.length - 1]?.rate || 0}%
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <div className="text-sm text-gray-600">Active Drivers</div>
                <div className="text-xl font-semibold text-gray-900">
                  {Math.round(analytics.totalUsers * 0.3)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* User Growth Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Monthly User Growth
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Month
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    New Users
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Users
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {analytics.userGrowth.slice(-6).map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.month}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.new}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.total}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Cancellation Rates Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Monthly Cancellation Rates
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Month
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cancelled
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rate %
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {analytics.cancellationRates.slice(-6).map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.month}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.cancelled}/{item.total}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 text-xs leading-5 font-semibold rounded-full ${
                        parseFloat(item.rate) > 10 ? 'bg-red-100 text-red-800' : 
                        parseFloat(item.rate) > 5 ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-green-100 text-green-800'
                      }`}>
                        {item.rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnalyticsReports; 